import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@material-ui/core/Box';
import { ResponsiveFilterLayout } from 'common/layouts';
import { CreationSavedSearchDrawer, SavedSearchDrawer } from 'features/saved-searches';
import { useShowAlert } from 'hooks';
import { useSearchProject } from 'hooks/searchProject';
import usePageTitle from 'hooks/usePageTitle';
import LogRocket from 'logrocket';
import strings from 'strings';
import { When } from 'UI/components/atoms';
import FPQuickView from 'UI/components/organisms/inventoryProfiles/FPQuickView';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import { MULTIPLE_SELECTION_TYPES, PageTitles } from 'UI/constants/defaults';
import { EntityType } from 'UI/constants/entityTypes';
import { getProfileQuickViewByEntity } from 'UI/constants/LogRocketCustomEvents';
import { SvgSearch } from 'UI/res';

import { PRIORITY_MAP } from './components/Filters/Filters.constants';
import { useFiltersForm } from './components/Filters/Filters.hooks';
import { createFiltersRequestPayload } from './components/Filters/Filters.utils';
import {
  Filters,
  Pagination,
  ResumeBulkActions,
  ResumeSearchToolbar,
  ResumesList
} from './components';
import {
  useResumeSearchCatalogs,
  useResumeSearchSavedSearches,
  useResumeSelection,
  useResumeSort
} from './hooks';
import { getResumeSortOrder, SLOT_PROPS } from './ResumeSearch.constants';
import { searchResumes } from './ResumeSearch.services';
import { buildSearchProjectQueryParams, normalizeResumeSearchResponse } from './ResumeSearch.utils';

const ResumeSearch = () => {
  usePageTitle({ title: PageTitles.ResumeSearch });
  const scrollRef = useRef(null);
  const { showAlert } = useShowAlert();
  const [listState, setListState] = useState({
    data: [],
    isLoading: false,
    totalResults: 0
  });
  const [quickViewCandidateId, setQuickViewCandidateId] = useState(null);
  const [isFloatingFiltersPanelOpen, setIsFloatingFiltersPanelOpen] = useState(false);
  const hasAppliedFiltersRef = useRef(false);
  const activeSearchRef = useRef(null);
  const handleRemoveSearchRef = useRef(null);
  const { candidateTypeColors, candidateStatusColors } = useResumeSearchCatalogs();

  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10
  });

  const { sortId, order, setSortId } = useResumeSort();

  const quickViewItems = useMemo(
    () =>
      listState.data
        .map(resume => resume.candidate?.id)
        .filter(Boolean)
        .map(id => ({ id })),
    [listState.data]
  );

  const handleViewDetails = useCallback(candidateId => {
    if (!candidateId) return;

    LogRocket.track(getProfileQuickViewByEntity(EntityType.Candidate));
    setQuickViewCandidateId(candidateId);
  }, []);

  const handleCloseQuickView = useCallback(() => {
    setQuickViewCandidateId(null);
  }, []);

  const {
    isSelected,
    toggleSelection,
    handleSelectPage,
    handleSelectAll,
    handleClearSelection,
    areSomeSelected,
    totalSelected,
    selectedIdsOnPage,
    selectedItems,
    canUseSearchProjectActions
  } = useResumeSelection({
    data: listState.data,
    totalResults: listState.totalResults
  });

  const setHasAppliedFilters = useCallback(value => {
    hasAppliedFiltersRef.current = value;
  }, []);

  const updateResumes = useCallback(
    results => {
      setHasAppliedFilters(true);
      setListState({
        data: results.data,
        totalResults: results.total,
        isLoading: false
      });
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    },
    [setHasAppliedFilters]
  );

  const clearResumes = useCallback(() => {
    setHasAppliedFilters(false);
    handleClearSelection();

    if (activeSearchRef.current && handleRemoveSearchRef.current) {
      handleRemoveSearchRef.current();
    }

    setListState({ data: [], isLoading: false, totalResults: 0 });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, [handleClearSelection, setHasAppliedFilters]);

  const handleOnSubmitFilters = useCallback(() => {
    setIsFloatingFiltersPanelOpen(false);
  }, []);

  const filtersFormOptions = useMemo(
    () => ({
      order,
      size: pagination.itemsPerPage,
      onGetResults: updateResumes,
      onClearResults: clearResumes,
      onSubmit: handleOnSubmitFilters,
      onClearSelection: handleClearSelection
    }),
    [
      order,
      pagination.itemsPerPage,
      updateResumes,
      clearResumes,
      handleOnSubmitFilters,
      handleClearSelection
    ]
  );

  const useFiltersFormInstance = useFiltersForm(filtersFormOptions);

  const {
    applySavedSearch: applySavedSearchToForm,
    formValues,
    getFiltersFormValues,
    getSerializableSearch,
    handleAdvancedSearch,
    handleClearSearch,
    handleFilterChange,
    hasActiveFilters
  } = useFiltersFormInstance;

  const searchProjectQueryParams = useMemo(
    () =>
      buildSearchProjectQueryParams({
        filtersFormValues: getFiltersFormValues(),
        itemsPerPage: pagination.itemsPerPage,
        order,
        totalResults: listState.totalResults
      }),
    [getFiltersFormValues, listState.totalResults, order, pagination.itemsPerPage]
  );

  const { SearchProjectAction, SearchProjectForms } = useSearchProject({
    filteredItems: selectedItems,
    queryParams: searchProjectQueryParams
  });

  const serializableFilters = useMemo(() => {
    if (!formValues) {
      return {};
    }

    return getSerializableSearch();
  }, [formValues, getSerializableSearch]);

  const handleApplySavedSearch = useCallback(
    async savedSearch => {
      const searchPayload = savedSearch?.search;
      const isSearchPayloadObject =
        typeof searchPayload === 'object' &&
        searchPayload !== null &&
        !Array.isArray(searchPayload);

      if (!isSearchPayloadObject) {
        return;
      }

      applySavedSearchToForm(searchPayload);
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      await handleAdvancedSearch();
      setIsFloatingFiltersPanelOpen(false);
    },
    [applySavedSearchToForm, handleAdvancedSearch]
  );

  const {
    activeSearch,
    isDirty,
    savedSearchesUiState,
    buttonDefinitions: savedSearchButtonDefinitions,
    handleApplySearch,
    handleEditSearch,
    handleRemoveSearch,
    handleCloseSavedSearchDrawer,
    handleCloseSavedSearchForm,
    handleDeleteSearch,
    handleEditSavedSearchDrawerOpen
  } = useResumeSearchSavedSearches({
    filters: serializableFilters,
    onAutoApplySearch: handleApplySavedSearch
  });

  activeSearchRef.current = activeSearch;
  handleRemoveSearchRef.current = handleRemoveSearch;

  const previousActiveSearchRef = useRef(activeSearch);

  const fetchResumes = useCallback(
    async (currentPageParam = 1, itemsPerPageParam = 10, orderParam = order) => {
      try {
        setListState(prev => ({ ...prev, isLoading: true }));
        const offset = (currentPageParam - 1) * itemsPerPageParam;
        const response = await searchResumes({
          ...createFiltersRequestPayload(getFiltersFormValues(), PRIORITY_MAP),
          order: orderParam,
          size: itemsPerPageParam,
          offset
        });
        const normalized = normalizeResumeSearchResponse(response);
        setListState({
          data: normalized.data,
          totalResults: normalized.total,
          isLoading: false
        });
      } catch (error) {
        showAlert({
          error,
          title: strings.resumeSearch.title,
          severity: 'error'
        });
        setListState(prev => ({
          ...prev,
          data: [],
          isLoading: false
        }));
      }
    },
    [order, showAlert, getFiltersFormValues]
  );

  const handleSortChange = useCallback(
    newSortId => {
      setSortId(newSortId);
      if (!hasAppliedFiltersRef.current) return;
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      fetchResumes(1, pagination.itemsPerPage, getResumeSortOrder(newSortId));
    },
    [fetchResumes, pagination.itemsPerPage, setSortId]
  );

  const dateRangeValue = formValues?.createdAt?.value ?? null;

  const handleDateRangeChange = useCallback(
    async nextRange => {
      handleFilterChange('createdAt', {
        fieldName: 'createdAt',
        isDateRange: true,
        value: nextRange
      });
      if (!hasActiveFilters) return;
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      await handleAdvancedSearch();
    },
    [handleFilterChange, hasActiveFilters, handleAdvancedSearch]
  );

  useEffect(() => {
    handleEditSearch(serializableFilters);
  }, [serializableFilters, handleEditSearch]);

  useEffect(() => {
    if (previousActiveSearchRef.current && !activeSearch) {
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }

    previousActiveSearchRef.current = activeSearch;
  }, [activeSearch]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [listState.data]);

  const handlePageChange = useCallback(
    newPage => {
      setPagination(prev => ({ ...prev, currentPage: newPage }));
      fetchResumes(newPage, pagination.itemsPerPage);
    },
    [fetchResumes, pagination.itemsPerPage]
  );

  const handleItemsPerPageChange = useCallback(
    newItemsPerPage => {
      setPagination(prev => ({ ...prev, itemsPerPage: newItemsPerPage, currentPage: 1 }));
      fetchResumes(1, newItemsPerPage);
    },
    [fetchResumes]
  );

  const handleToggleFloatingFiltersPanel = useCallback(() => {
    setIsFloatingFiltersPanelOpen(prev => !prev);
  }, []);

  const isLoading = listState.isLoading || useFiltersFormInstance.uiState.submitting;
  const totalPages = Math.ceil(listState.totalResults / pagination.itemsPerPage);

  const paginationComponent = useMemo(() => {
    if (isLoading || totalPages === 0) return null;
    return (
      <Pagination
        currentPage={pagination.currentPage}
        totalPages={totalPages}
        totalItems={listState.totalResults}
        itemsPerPage={pagination.itemsPerPage}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
      />
    );
  }, [
    isLoading,
    totalPages,
    pagination.currentPage,
    pagination.itemsPerPage,
    listState.totalResults,
    handlePageChange,
    handleItemsPerPageChange
  ]);

  const handleSelectionChange = useCallback(
    selectionType => {
      const handlerBySelectionTypeMap = {
        [MULTIPLE_SELECTION_TYPES.all]: handleSelectAll,
        [MULTIPLE_SELECTION_TYPES.page]: handleSelectPage,
        [MULTIPLE_SELECTION_TYPES.unselectAll]: handleClearSelection
      };
      const action = handlerBySelectionTypeMap[selectionType];
      if (action) action();
    },
    [handleSelectAll, handleSelectPage, handleClearSelection]
  );

  const selectionProps = useMemo(
    () => ({
      isSelected,
      onToggleSelection: toggleSelection,
      onSelectionChange: handleSelectionChange,
      areSomeSelected,
      totalSelected,
      selectedIdsOnPage
    }),
    [
      isSelected,
      toggleSelection,
      handleSelectionChange,
      areSomeSelected,
      totalSelected,
      selectedIdsOnPage
    ]
  );

  const savedSearchAppliedProps = useMemo(
    () => ({
      isDirty,
      onRemoveSearch: () => {
        handleRemoveSearch();
        activeSearchRef.current = null;
        handleClearSearch();
      },
      onSaveSearch: handleEditSavedSearchDrawerOpen,
      searchName: activeSearch?.name
    }),
    [activeSearch, isDirty, handleEditSavedSearchDrawerOpen, handleRemoveSearch, handleClearSearch]
  );

  const renderRightPanel = () => {
    return (
      <Box display="flex" flexDirection="column" height="100%">
        <ResumeSearchToolbar
          dateRangeValue={dateRangeValue}
          handleDateRangeChange={handleDateRangeChange}
          isLoading={isLoading}
          sortId={sortId}
          handleSortChange={handleSortChange}
          savedSearchButtonDefinitions={savedSearchButtonDefinitions}
          activeSearch={activeSearch}
          savedSearchAppliedProps={savedSearchAppliedProps}
        />
        <Box flex={1} overflow="hidden">
          {!hasAppliedFiltersRef.current && !isLoading ? (
            <EmptyPlaceholder
              title={strings.resumeSearch.emptyState.title}
              subtitle={strings.resumeSearch.emptyState.subtitle}
              customEmptyState={<SvgSearch height={400} />}
            />
          ) : (
            <ResumesList
              resumes={listState.data}
              totalResults={listState.totalResults}
              pagination={paginationComponent}
              isLoading={isLoading}
              candidateTypeColors={candidateTypeColors}
              candidateStatusColors={candidateStatusColors}
              scrollRef={scrollRef}
              selectionAction={
                <Box display="flex" alignItems="center">
                  {canUseSearchProjectActions ? <SearchProjectAction /> : null}
                  <ResumeBulkActions selectedItems={selectedItems} totalSelected={totalSelected} />
                </Box>
              }
              onViewDetails={handleViewDetails}
              {...selectionProps}
            />
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <ResponsiveFilterLayout
        leftPanel={<Filters useFiltersFormInstance={useFiltersFormInstance} />}
        rightPanel={renderRightPanel()}
        isFloatingPanelOpen={isFloatingFiltersPanelOpen}
        onToggleFloatingPanel={handleToggleFloatingFiltersPanel}
        slotProps={SLOT_PROPS}
      />

      <When condition={savedSearchesUiState.isSavedSearchDrawerOpen}>
        <SavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.ResumeSearch}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchDrawer}
          onDeleteSearch={handleDeleteSearch}
          open={savedSearchesUiState.isSavedSearchDrawerOpen}
        />
      </When>

      <When
        condition={
          savedSearchesUiState.isCreationSavedSearchDrawerOpen ||
          savedSearchesUiState.isEditSavedSearchDrawerOpen
        }
      >
        <CreationSavedSearchDrawer
          activeSearch={activeSearch}
          entityType={EntityType.ResumeSearch}
          onApplySearch={handleApplySearch}
          onClose={handleCloseSavedSearchForm}
          onSaveCompleted={handleCloseSavedSearchForm}
          open
          savedSearchId={savedSearchesUiState.isEditSavedSearchDrawerOpen ? activeSearch?.id : null}
          search={serializableFilters}
        />
      </When>

      <SearchProjectForms />

      <When condition={Boolean(quickViewCandidateId)}>
        <FPQuickView
          drawerProps={{ open: Boolean(quickViewCandidateId) }}
          entityType={EntityType.Candidate}
          id={quickViewCandidateId}
          itemsToNavigate={quickViewItems}
          onClose={handleCloseQuickView}
        />
      </When>
    </>
  );
};

export default ResumeSearch;
